import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Spinner,
  Pagination,
} from "react-bootstrap";
import {
  ArrowLeft,
  BoxArrowRight,
  Bell,
  PersonCircle,
  FileEarmarkSpreadsheet,
  ColumnsGap,
  PersonPlusFill,
  InfoCircle,
} from "react-bootstrap-icons";
import SettingsGear from "../components/SettingsGear";
import * as XLSX from "xlsx";
import axios from "axios";
import "../App.css";
import LOGO_URL from "../img/tmis_logo_orig.png";

const NAV_BLUE = "#183B72";
const PANEL_BG = "#F1F3F6";
const PANEL_SURFACE = "#E7EAEE";
const BRAND = "#3D74F5";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_TG_LIST = `${API_BASE}/getTGByList`;
const ENDPOINT_SAVE_UNREGISTERED = `${API_BASE}/saveUnregisteredTG`;

function safeParse(json, fallback = {}) {
  try {
    return JSON.parse(json || "") ?? fallback;
  } catch {
    return fallback;
  }
}

function getStoredUser() {
  const user = safeParse(localStorage.getItem("user"), {});
  const displayName =
    localStorage.getItem("display_name") ||
    user.display_name ||
    user.username ||
    localStorage.getItem("username") ||
    "User Acc";
  const userId = user.user_id ?? user.id ?? "";
  const isAdmin =
    localStorage.getItem("isAdmin") === "1" ||
    localStorage.getItem("role") === "admin" ||
    user.isAdmin === true;
  return { displayName, userId, isAdmin };
}

function toDisplay(d) {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

function toISO(d) {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
}

function excelSerialToDates(v) {
  const date = new Date(Math.round((v - 25569) * 86400 * 1000));
  if (isNaN(date.getTime())) return { display: "-", iso: "" };
  return { display: toDisplay(date), iso: toISO(date) };
}

function parseDateLike(v) {
  if (!v) return { display: "-", iso: "" };
  const s = String(v).trim();

  const m1 = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m1) {
    const d = new Date(Number(m1[3]), Number(m1[2]) - 1, Number(m1[1]));
    if (!isNaN(d.getTime())) return { display: toDisplay(d), iso: toISO(d) };
  }

  const m2 = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (m2) {
    const d = new Date(Number(m2[1]), Number(m2[2]) - 1, Number(m2[3]));
    if (!isNaN(d.getTime())) return { display: toDisplay(d), iso: toISO(d) };
  }

  const d = new Date(s);
  if (!isNaN(d.getTime())) return { display: toDisplay(d), iso: toISO(d) };

  return { display: s, iso: "" };
}

function normalizeHeader(h) {
  const k = String(h || "").trim().toLowerCase();

  if (
    [
      "licence number",
      "license number",
      "licence_no",
      "license_no",
      "licence #",
      "license #",
      "licence no",
      "license no",
    ].includes(k)
  )
    return "licence_number";

  if (["salutation", "title"].includes(k)) return "salutation";

  if (["name", "full name"].includes(k)) return "name";

  if (["email address", "email", "e-mail", "emailaddress"].includes(k))
    return "email_address";

  if (
    [
      "mobile number",
      "mobile",
      "phone",
      "phone number",
      "contact number",
      "mobile no",
      "phone no",
    ].includes(k)
  )
    return "mobile_number";

  if (
    [
      "this cycle expiry date",
      "this_cycle_expiry_date",
      "licence expiry date",
      "license expiry date",
      "tg license expiry date",
      "tg licence expiry date",
      "tg expiry date",
      "expiry date",
    ].includes(k)
  )
    return "this_cycle_expiry_date";

  if (["tier", "level"].includes(k)) return "tier";

  if (["badge number", "badge no", "badge_no", "badge"].includes(k))
    return "licence_number";

  if (["tg_expiry_date", "tg licence", "tg license", "tg expiry"].includes(k))
    return "this_cycle_expiry_date";

  return k;
}

function formatName(name) {
  if (!name || name === "-") return name || "-";
  return String(name)
    .toLowerCase()
    .split(/\s+/)
    .map((part) =>
      part ? part.charAt(0).toUpperCase() + part.slice(1) : ""
    )
    .join(" ");
}

export default function Comparison() {
  const { displayName, userId, isAdmin } = useMemo(getStoredUser, []);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    setSelected(new Set());
    setPage(1);
  }, [rows]);

  const totalPages = useMemo(() => {
    if (!rows.length) return 1;
    if (pageSize === "all") return 1;
    return Math.max(1, Math.ceil(rows.length / pageSize));
  }, [rows.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIndex = pageSize === "all" ? 0 : (page - 1) * pageSize;

  const currentPageRows = useMemo(() => {
    if (pageSize === "all") return rows;
    return rows.slice(startIndex, startIndex + pageSize);
  }, [rows, startIndex, pageSize]);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.replace("/");
  };

  const handleBack = () => {
    if (window.history.length > 1) window.history.back();
    else window.location.replace("/");
  };

  const handleUploadExcelClick = () => fileRef.current?.click();

  const parseWorksheetToRows = (ws) => {
    const json = XLSX.utils.sheet_to_json(ws, { defval: "" });

    return json.map((obj, idx) => {
      const normalized = {};
      Object.keys(obj).forEach((key) => {
        normalized[normalizeHeader(key)] = obj[key];
      });

      const licence_number =
        String(normalized["licence_number"] || "").trim() || "-";
      const salutation = String(normalized["salutation"] || "").trim() || "-";
      const name = formatName(String(normalized["name"] || "").trim() || "-");
      const email = (
        String(
          normalized["email_address"] ||
            normalized["email"] ||
            normalized["emailaddress"] ||
            ""
        ).trim() || "-"
      ).toLowerCase();
      const mobile =
        String(
          normalized["mobile_number"] ||
            normalized["mobile"] ||
            normalized["phone"] ||
            ""
        ).trim() || "-";
      const tier = String(normalized["tier"] || "").trim() || "-";

      let expiry_display = "-",
        expiry_iso = "";
      const rawExpiry = normalized["this_cycle_expiry_date"] ?? "";

      if (typeof rawExpiry === "number") {
        ({ display: expiry_display, iso: expiry_iso } =
          excelSerialToDates(rawExpiry));
      } else if (typeof rawExpiry === "string" && rawExpiry.trim() !== "") {
        ({ display: expiry_display, iso: expiry_iso } =
          parseDateLike(rawExpiry));
      }

      return {
        id: idx + 1,
        licence_number,
        salutation,
        name,
        email,
        mobile,
        tier,
        expiry_display,
        expiry_iso,
        status: "Unregister",

        badge_no: licence_number,
        phone: mobile,
        tg_expiry_display: expiry_display,
        tg_expiry_iso: expiry_iso,
      };
    });
  };

  const handleUploadExcelFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      setRows(ws ? parseWorksheetToRows(ws) : []);
    } catch (err) {
      console.error("Failed to parse spreadsheet:", err);
      setRows([]);
    } finally {
      e.target.value = "";
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllCurrentPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected =
        currentPageRows.length > 0 &&
        currentPageRows.every((r) => next.has(r.id));

      if (allSelected) {
        currentPageRows.forEach((r) => next.delete(r.id));
      } else {
        currentPageRows.forEach((r) => next.add(r.id));
      }
      return next;
    });
  };

  const handleCompare = async () => {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("auth_token") ||
      "";
    if (!API_BASE) return;

    try {
      setLoadingCompare(true);
      const { data } = await axios.get(ENDPOINT_TG_LIST, {
        params: { type: "list", isAdmin: !!isAdmin, userId: userId || "" },
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
      });

      if (!(data?.success && Array.isArray(data?.data))) {
        alert(data?.message || "Failed to fetch TG list.");
        return;
      }

      const backendLicenceNos = new Set(
        data.data
          .map((d) =>
            String(
              d.licence_number ??
                d.licenceNo ??
                d.license_number ??
                d.badge_no ??
                ""
            ).trim()
          )
          .filter(Boolean)
      );

      setRows((prev) =>
        prev.map((r) =>
          backendLicenceNos.has(String(r.licence_number || "").trim())
            ? { ...r, status: "Registered" }
            : { ...r, status: "Unregister" }
        )
      );
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        alert("Your session has expired. Please log in again.");
        handleLogout();
        return;
      }
      console.error("Compare failed:", err);
      alert("Failed to fetch TG list from server.");
    } finally {
      setLoadingCompare(false);
    }
  };

  const handleRegisterTGs = async () => {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("auth_token") ||
      "";

    const toSave = rows
      .filter(
        (r) =>
          selected.has(r.id) &&
          (r.status || "Unregister").toLowerCase() === "unregister"
      )
      .map((r) => ({
        salutation: r.salutation || "",
        name: formatName(r.name || ""),
        email: (r.email || "").toLowerCase(),
        tier: r.tier || "",
        badgeNo: r.licence_number || "",
        phoneNo: r.mobile || "",
        licenceExpiryDate: r.expiry_iso || "",
      }));

    if (toSave.length === 0) {
      alert("Select at least one 'Unregister' row to save.");
      return;
    }

    const CHUNK_SIZE = 100;

    try {
      setSaving(true);

      let totalSaved = 0;

      for (let i = 0; i < toSave.length; i += CHUNK_SIZE) {
        const chunk = toSave.slice(i, i + CHUNK_SIZE);

        const { data } = await axios.post(
          ENDPOINT_SAVE_UNREGISTERED,
          { user: chunk },
          {
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            timeout: 30000,
          }
        );

        if (!data?.success) {
          throw new Error(data?.message || "Failed to save some TGs");
        }

        totalSaved += data.data ?? chunk.length;
      }

      alert(`Saved ${totalSaved} unregistered TG(s).`);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        alert("Your session has expired. Please log in again.");
        handleLogout();
        return;
      }

      if (axios.isAxiosError(err) && err.response?.status === 413) {
        alert(
          "The batch is still too large for the server. Please try again with a smaller chunk size (e.g. 50)."
        );
      } else {
        console.error("Save failed:", err);
        alert("Server error. Please try again later.");
      }
    } finally {
      setSaving(false);
    }
  };

  const allCurrentPageSelected =
    currentPageRows.length > 0 &&
    currentPageRows.every((r) => selected.has(r.id));

  const firstRowNo =
    rows.length === 0 ? 0 : pageSize === "all" ? 1 : Math.min(startIndex + 1, rows.length);

  const lastRowNo =
    rows.length === 0
      ? 0
      : pageSize === "all"
      ? rows.length
      : Math.min(startIndex + currentPageRows.length, rows.length);

  return (
    <div
      className="custom-font"
      style={{ minHeight: "100vh", background: "#f7f9fc" }}
    >
      <Container>
        <div
          className="d-flex align-items-center justify-content-between py-2"
          style={{ gap: 16 }}
        >
          <img src={LOGO_URL} alt="TMIS" style={{ height: 50 }} />
          <div className="d-flex align-items-center" style={{ gap: 25 }}>
            <Bell size={20} color="#1A3584" />
            <SettingsGear />
            <BoxArrowRight
              size={20}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
            />
            <span>{displayName}</span>
            <PersonCircle size={23} color="#1A3584" />
          </div>
        </div>
      </Container>

      <div
        style={{
          height: 64,
          background: NAV_BLUE,
          color: "white",
          display: "flex",
          alignItems: "center",
          marginTop: 8,
        }}
      >
        <Container>
          <div
            className="container-xxl d-flex align-items-center"
            style={{ gap: 12 }}
          >
            <span
              role="button"
              aria-label="Go back"
              tabIndex={0}
              onClick={handleBack}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                background: "transparent",
                cursor: "pointer",
              }}
              className="back-icon"
            >
              <ArrowLeft size={18} color="#ffffff" />
            </span>
            <h4 className="m-0 fw-bold">COMPARISON FROM EXCEL SHEET</h4>
          </div>
        </Container>
      </div>

      <Container style={{ paddingTop: 22, paddingBottom: 40 }}>
        <Row className="g-4">
          <Col xs={12}>
            <Card
              style={{
                background: PANEL_BG,
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
                borderRadius: 18,
              }}
            >
              <Card.Body style={{ background: "white", borderRadius: 16 }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <Button
                      variant="outline-success"
                      onClick={handleUploadExcelClick}
                      className="d-inline-flex align-items-center gap-2"
                    >
                      <FileEarmarkSpreadsheet size={16} />
                      Upload Excel
                    </Button>
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleUploadExcelFile}
                      hidden
                    />
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center gap-1">
                      <span className="small text-muted">Rows per page:</span>
                      <select
                        value={pageSize === "all" ? "all" : String(pageSize)}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "all") {
                            setPageSize("all");
                            setPage(1);
                          } else {
                            const num = Number(val) || 50;
                            setPageSize(num);
                            setPage(1);
                          }
                        }}
                        className="form-select form-select-sm"
                        style={{ width: "auto" }}
                      >
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="all">All</option>
                      </select>
                    </div>

                    <Button
                      variant="primary"
                      style={{ background: BRAND, borderColor: BRAND }}
                      className="d-inline-flex align-items-center gap-2"
                      onClick={handleCompare}
                      disabled={rows.length === 0 || loadingCompare}
                      title={
                        rows.length === 0
                          ? "Upload a file first"
                          : "Compare licence numbers with TMIS"
                      }
                    >
                      {loadingCompare ? (
                        <Spinner animation="border" size="sm" variant="light" />
                      ) : (
                        <ColumnsGap size={16} />
                      )}
                      Compare
                    </Button>
                  </div>
                </div>

                <div className="table-responsive">
                  <Table hover align="start" className="mb-0">
                    <thead style={{ background: PANEL_SURFACE }}>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={allCurrentPageSelected}
                            onChange={toggleAllCurrentPage}
                          />
                        </th>
                        <th>No</th>
                        <th>Licence Number</th>
                        <th>Salutation</th>
                        <th>Name</th>
                        <th>Email Address</th>
                        <th>Mobile Number</th>
                        <th>This Cycle Expiry Date</th>
                        <th>Tier</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentPageRows.length ? (
                        currentPageRows.map((r, idx) => (
                          <tr key={r.id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selected.has(r.id)}
                                onChange={() => toggleRow(r.id)}
                              />
                            </td>
                            <td>
                              {pageSize === "all"
                                ? idx + 1
                                : startIndex + idx + 1}
                            </td>
                            <td>{r.licence_number || "-"}</td>
                            <td>{r.salutation || "-"}</td>
                            <td>{formatName(r.name || "-")}</td>
                            <td>{r.email || "-"}</td>
                            <td>{r.mobile || "-"}</td>
                            <td>{r.expiry_display || "-"}</td>
                            <td>{r.tier || "-"}</td>
                            <td>
                              <span
                                className="badge"
                                style={{
                                  background:
                                    r.status === "Registered"
                                      ? "rgba(16,185,129,.15)"
                                      : "rgba(239,68,68,.12)",
                                  color:
                                    r.status === "Registered"
                                      ? "#065f46"
                                      : "#991b1b",
                                  border:
                                    r.status === "Registered"
                                      ? "1px solid rgba(16,185,129,.35)"
                                      : "1px solid rgba(239,68,68,.35)",
                                }}
                              >
                                {r.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={10} className="text-center py-4">
                            Upload an Excel/CSV to see rows here.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                {rows.length > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="small text-muted">
                      Showing {firstRowNo}–{lastRowNo} of {rows.length} rows
                    </div>

                    {pageSize !== "all" && (
                      <Pagination size="sm" className="mb-0">
                        <Pagination.First
                          disabled={page === 1}
                          onClick={() => setPage(1)}
                        />
                        <Pagination.Prev
                          disabled={page === 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        />
                        <Pagination.Item active>{page}</Pagination.Item>
                        <Pagination.Next
                          disabled={page === totalPages}
                          onClick={() =>
                            setPage((p) => Math.min(totalPages, p + 1))
                          }
                        />
                        <Pagination.Last
                          disabled={page === totalPages}
                          onClick={() => setPage(totalPages)}
                        />
                      </Pagination>
                    )}
                  </div>
                )}

                <div className="d-flex flex-wrap align-items-center gap-3 mt-3 small text-muted">
                  <span className="d-inline-flex align-items-center gap-2">
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        background: "rgba(16,185,129,.15)",
                        border: "1px solid rgba(16,185,129,.35)",
                        display: "inline-block",
                        borderRadius: 3,
                      }}
                    />
                    <strong className="text-success">Registered</strong> Tourist
                    guide who registered in TMIS
                  </span>
                  <span className="d-inline-flex align-items-center gap-2">
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        background: "rgba(239,68,68,.12)",
                        border: "1px solid rgba(239,68,68,.35)",
                        display: "inline-block",
                        borderRadius: 3,
                      }}
                    />
                    <strong className="text-danger">Unregistered</strong> Not
                    found in TMIS
                  </span>
                  <span className="d-inline-flex align-items-center gap-2 ms-auto">
                    <InfoCircle size={14} />
                    Select rows then click <strong>Save TGs</strong> to save
                    unregistered entries.
                  </span>
                </div>

                <div className="d-flex justify-content-end mt-3">
                  <Button
                    variant="primary"
                    style={{ background: BRAND, borderColor: BRAND }}
                    className="d-inline-flex align-items-center gap-2"
                    onClick={handleRegisterTGs}
                    disabled={selected.size === 0 || saving}
                    title={
                      selected.size === 0
                        ? "Select at least one row"
                        : "Save selected unregistered TGs"
                    }
                  >
                    {saving ? (
                      <Spinner animation="border" size="sm" variant="light" />
                    ) : (
                      <PersonPlusFill size={16} />
                    )}
                    Save TGs ({selected.size})
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
